import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { SafeOverview } from '@safe-global/store/gateway/AUTO_GENERATED/safes'
import {
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableSortIcon,
  tableVariants,
} from '@safe-global/views/components/ui/table'
import tableCss from './styles.module.css'
import type { AllSafeItems } from '@/hooks/safes'
import { cn } from '@safe-global/views/utils/cn'
import {
  SAFE_ACCOUNT_COLUMNS,
  SELECT_COLUMN,
  type SafeAccountColumnId,
} from '@safe-global/views/features/myAccounts/components/SafeAccountsTable/columns'
import {
  compareGroups,
  getContextMenuChainIds,
  overviewKey,
  useSafeAccountRows,
  type AccountGroup,
  type AccountLine,
  type SafeSortColumn,
} from './useSafeAccountRows'
import SafeAccountTableRow, { type RowCheckbox } from './SafeAccountTableRow'
import ReorderableBody, { toggleExpanded } from './ReorderableBody'
import { bandHeaderAt } from './SimilarityBand'
import { orderGroupsBySimilarity } from '@safe-global/views/features/myAccounts/components/SafeAccountsTable/orderGroupsBySimilarity'
import { weaveReorderedKeys } from '@/utils/reorder'
import type { SimilarWarning } from '@/features/address-poisoning'
import EntryDialog from '@/components/address-book/EntryDialog'
import { useAddressBookWriteScope } from '@/features/spaces'

/** Renaming a safe = editing its address-book entry across every chain it lives on. */
type RenameTarget = { name: string; address: string; chainIds: string[] }

const toRenameTarget = (line: AccountLine): RenameTarget => ({
  name: line.contextMenu.name,
  address: line.contextMenu.address,
  chainIds: getContextMenuChainIds(line.contextMenu),
})

type SortState = { orderBy: SafeSortColumn | null; order: 'asc' | 'desc' }

/**
 * Optional selection (checkbox) mode. The table deals only in leaf keys (`${chainId}:${address}`,
 * which equal a leaf AccountLine's `key`); the caller owns any multi-chain aggregate key. A group
 * row's checkbox state is derived from its children.
 */
export type SafeAccountsSelection = {
  /** Selected leaf keys (singles + per-chain children). */
  selectedKeys: Set<string>
  /** Fired on a checkbox toggle; `line` is the toggled row (leaf or group), `nextChecked` the desired state. */
  onToggle: (line: AccountLine, nextChecked: boolean) => void
  /** Seat cap reached — unselected leaves and empty groups render disabled, except another chain of a selected address (same seat). */
  isAtLimit?: boolean
  /** Leaf keys to disable (and dim) regardless of the cap — e.g. safes already in the workspace. */
  disabledKeys?: Set<string>
  /** Tooltip explaining why `disabledKeys` rows are locked (e.g. "This safe is already part of your workspace"). */
  disabledReason?: string
}

const getRowCheckbox = (group: AccountGroup, line: AccountLine, selection: SafeAccountsSelection): RowCheckbox => {
  const { selectedKeys, isAtLimit, disabledKeys, disabledReason } = selection

  if (line.variant === 'group') {
    const childKeys = group.children.map((child) => child.key)
    const selectedCount = childKeys.filter((key) => selectedKeys.has(key)).length
    const checked = childKeys.length > 0 && selectedCount === childKeys.length
    const lockedByReason = childKeys.length > 0 && childKeys.every((key) => disabledKeys?.has(key))
    return {
      checked,
      indeterminate: selectedCount > 0 && !checked,
      disabled: lockedByReason || (Boolean(isAtLimit) && selectedCount === 0),
      disabledReason: lockedByReason ? disabledReason : undefined,
      ariaLabel: line.displayName,
    }
  }

  const lockedByReason = Boolean(disabledKeys?.has(line.key))
  const checked = selectedKeys.has(line.key)
  const holdsSeat = group.children.some((child) => child.key !== line.key && selectedKeys.has(child.key))
  return {
    checked,
    indeterminate: false,
    disabled: lockedByReason || (Boolean(isAtLimit) && !checked && !holdsSeat),
    disabledReason: lockedByReason ? disabledReason : undefined,
    ariaLabel: line.displayName,
  }
}

/** Enables drag-and-drop reordering of the top-level accounts. */
export type SafeAccountsReorder = {
  /** Fired on drop with the reordered top-level account addresses, in display order. */
  onReorder: (orderedAddresses: string[]) => void
}

export type SafeAccountsTableProps = {
  items: AllSafeItems
  /** Columns to show, in the canonical order. Defaults to the full set. */
  columns?: SafeAccountColumnId[]
  /** Overrides the actions column width — e.g. when `renderActions` renders a button instead of a menu. */
  actionsWidth?: string
  /** Replaces the default context-menu actions cell for each row (e.g. an "Add to workspace" button). */
  renderActions?: (line: AccountLine) => ReactNode
  /** Replaces the identity cell. Rows stop navigating so the cell can hold its own controls. */
  renderName?: (line: AccountLine) => ReactNode
  /** Lowercased address → cross-list look-alike peers; drives the inline ⚠️ + tooltip. */
  similarWarnings?: Map<string, SimilarWarning>
  /** Lowercased address → cluster id; contiguous same-cluster rows render inside a warning band. */
  similarityGroups?: Map<string, string>
  /** Lowercased vetted/pinned addresses — ordering-only: a band opens at its anchor's slot, anchor first. */
  anchorAddresses?: Set<string>
  /** Enables a leading checkbox column and makes rows selectable. */
  selection?: SafeAccountsSelection
  /**
   * Re-enables the hover rename pencil on a selection surface (selection normally suppresses it) and
   * floats the rename dialog above a shadcn Dialog (--z-overlay: 1400). Used by the "Manage my account
   * list" modal, whose table is both selectable and rendered inside a shadcn Dialog.
   */
  allowRenameInDialog?: boolean
  /** Enables a leading drag-handle column and makes top-level accounts reorderable. */
  reorder?: SafeAccountsReorder
  /**
   * Whether the column headers can re-sort the table. Defaults to `true`. Surfaces with their own
   * sort-mode control (the Name / Last visited / Manual dropdown) pass `false` whenever the active
   * mode isn't Name, so that mode owns the order instead of being silently overridden by a header
   * click (Last visited and Manual have no column equivalent).
   */
  sortableColumns?: boolean
  /** Fired when a row's link is clicked; receives the clicked line so callers can track the safe. */
  onLinkClick?: (line: AccountLine) => void
  /**
   * Renders the table flush inside a card: no column header, no bordered container, no row
   * dividers, and the Name column flexes to fill the available width instead of being fixed.
   * Used by the dashboard widget.
   */
  embedded?: boolean
  /**
   * Whether the standalone container draws its 1px outline. Defaults to `true`. The space
   * "Safe accounts" page passes `false` to sit flush on the page background while keeping the
   * card fill, header and dividers.
   */
  bordered?: boolean
  'data-testid'?: string
}

// Declared as a hoisted `function` (not a `const` arrow) on purpose: this component sits in a
// cross-feature import cycle (myAccounts ↔ spaces barrels), and webpack's React Refresh runtime
// eagerly reads every export at module-eval time. A `const` binding read mid-cycle throws
// "Cannot access before initialization" (TDZ) and crashes Storybook; a hoisted function is
// readable even while its module is still initializing. The app (Rspack) tolerates the cycle
// regardless. Must be `export default function` inline (not a trailing `export default X`) — the
// latter compiles to a TDZ `__WEBPACK_DEFAULT_EXPORT__` temp that reintroduces the crash. See
// docs/feature-architecture.md.
export default function SafeAccountsTable({
  items,
  columns,
  actionsWidth,
  renderActions,
  renderName,
  similarWarnings,
  similarityGroups,
  anchorAddresses,
  selection,
  allowRenameInDialog = false,
  reorder,
  sortableColumns = true,
  onLinkClick,
  embedded = false,
  bordered = true,
  'data-testid': testId = 'safe-accounts-table',
}: SafeAccountsTableProps) {
  const [overviewsByKey, setOverviewsByKey] = useState<Map<string, SafeOverview>>(new Map())

  // Rows report their lazily-fetched overviews here. RTK returns a stable object ref per cache entry
  // (a new ref only on a genuine refetch), so reference equality detects real updates; keep the
  // previous map when nothing new arrived, so a repeated report doesn't churn a re-render.
  const handleOverviewsLoaded = useCallback((overviews: SafeOverview[]) => {
    setOverviewsByKey((prev) => {
      let changed = false
      const next = new Map(prev)
      for (const overview of overviews) {
        const key = overviewKey(overview.chainId, overview.address.value)
        if (next.get(key) !== overview) {
          next.set(key, overview)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [])

  const hasWorkspacesColumn = !columns || columns.includes('workspaces')
  const { groups } = useSafeAccountRows(items, overviewsByKey, hasWorkspacesColumn)
  const [sort, setSort] = useState<SortState>({ orderBy: null, order: 'asc' })
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null)

  // Selection surfaces are modals (trusted picker, manage, onboarding) where rename isn't offered by
  // default; the hover rename pencil is otherwise only for the navigable welcome/workspace tables. The
  // "Manage my account list" modal opts back in via `allowRenameInDialog`.
  const canRename = allowRenameInDialog || !selection
  const onRename = canRename ? (line: AccountLine) => setRenameTarget(toRenameTarget(line)) : undefined
  const { scope: renameScope } = useAddressBookWriteScope(renameTarget?.address, renameTarget?.chainIds ?? [])

  const visibleColumns = useMemo(() => {
    const base = columns ? SAFE_ACCOUNT_COLUMNS.filter((c) => columns.includes(c.id)) : SAFE_ACCOUNT_COLUMNS
    const withActions = actionsWidth ? base.map((c) => (c.id === 'actions' ? { ...c, width: actionsWidth } : c)) : base
    // Reorder mode adds no column: the drag handle floats in the Name cell's left gutter, so the
    // table keeps the same width as the other sort modes (no differential horizontal scrollbar).
    if (selection) return [SELECT_COLUMN, ...withActions]
    return withActions
  }, [columns, actionsWidth, selection])

  const minWidth = useMemo(
    () => visibleColumns.reduce((sum, column) => sum + parseInt(column.width ?? '0', 10), 0),
    [visibleColumns],
  )

  const sortedGroups = useMemo(() => {
    if (!sortableColumns || !sort.orderBy) return groups
    const orderBy = sort.orderBy
    return [...groups].sort((a, b) => compareGroups(a, b, orderBy, sort.order))
  }, [groups, sort, sortableColumns])

  // When an external sort-mode control takes over ordering (Last visited / Manual), clear any active
  // column sort so a stale header arrow and order don't linger if column sorting re-enables.
  useEffect(() => {
    if (!sortableColumns) setSort({ orderBy: null, order: 'asc' })
  }, [sortableColumns])

  // Pull similarity clusters together (non-reorder body) so each renders as one contiguous band.
  const displayGroups = useMemo(
    () => orderGroupsBySimilarity(sortedGroups, similarityGroups, anchorAddresses),
    [sortedGroups, similarityGroups, anchorAddresses],
  )

  // Weave the dropped (non-clustered) rows into the stored order: the clusters' pin-to-top is
  // display-only and must not rewrite the user's manual arrangement (it would outlive the cluster).
  const handleReorder = useCallback(
    (reorderedDraggable: string[]) => {
      const storedOrder = sortedGroups.map((group) => group.parent.address)
      const isClustered = (address: string) => Boolean(similarityGroups?.get(address.toLowerCase()))
      reorder?.onReorder(weaveReorderedKeys(storedOrder, reorderedDraggable, isClustered))
    },
    [reorder, sortedGroups, similarityGroups],
  )

  const lines = useMemo<Array<{ line: AccountLine; groupKey: string; group: AccountGroup }>>(() => {
    const result: Array<{ line: AccountLine; groupKey: string; group: AccountGroup }> = []
    for (const group of displayGroups) {
      result.push({ line: group.parent, groupKey: group.parent.key, group })
      if (group.children.length > 0 && expanded.has(group.parent.key)) {
        for (const child of group.children) {
          result.push({ line: child, groupKey: group.parent.key, group })
        }
      }
    }
    return result
  }, [displayGroups, expanded])

  const handleSort = (column: SafeSortColumn) =>
    setSort((prev) => ({
      orderBy: column,
      order: prev.orderBy === column && prev.order === 'asc' ? 'desc' : 'asc',
    }))

  const toggle = (key: string) => setExpanded((prev) => toggleExpanded(prev, key))

  if (items.length === 0) return null

  return (
    <div data-testid={testId} className="w-full">
      <div
        className={cn(
          'w-full',
          embedded ? 'overflow-x-visible' : 'overflow-x-auto',
          !embedded && tableCss.container,
          // `bordered={false}` drops the panel's edge — for tables nested in something that draws its own.
          !embedded && !bordered && tableCss.containerBorderless,
        )}
      >
        {/* Raw <table> instead of the ui <Table> wrapper: we own the horizontal-scroll container
            above so `embedded` tables can opt out of it. The shadcn table sub-components are used
            throughout. */}
        <table
          className={cn(tableVariants({ variant: 'panel' }), tableCss.accounts)}
          style={{ tableLayout: 'fixed', minWidth: embedded ? undefined : minWidth }}
        >
          {/* Embedded (headerless) tables need a colgroup to keep fixed-layout column widths; the Name
              column is left unsized so it flexes to fill the card, while the stat columns stay fixed. */}
          {embedded && (
            <colgroup>
              {visibleColumns.map((column) => (
                <col key={column.id} style={column.id === 'name' ? undefined : { width: column.width }} />
              ))}
            </colgroup>
          )}

          {!embedded && (
            <TableHeader>
              <TableRow>
                {visibleColumns.map((column) => {
                  const active = sort.orderBy === column.sortKey
                  const canSort = column.sortable && column.sortKey && sortableColumns
                  return (
                    <TableHead
                      key={column.id}
                      aria-sort={active ? (sort.order === 'asc' ? 'ascending' : 'descending') : undefined}
                      // Indents the NAME label so it sits above the account name text rather than the
                      // avatar (see styles.module.css) — a leading checkbox column already offsets
                      // the cell, so it needs less.
                      data-name-head={column.id === 'name' ? (selection ? 'selection' : 'default') : undefined}
                      className="px-2 py-2.5"
                      style={{ width: column.width, textAlign: column.align ?? 'left' }}
                    >
                      {canSort ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSort(column.sortKey as SafeSortColumn)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              handleSort(column.sortKey as SafeSortColumn)
                            }
                          }}
                          data-testid={`account-sort-${column.id}`}
                          className="hover:text-foreground group/sort inline-flex cursor-pointer items-center gap-1 uppercase select-none"
                        >
                          {column.label}
                          <TableSortIcon direction={active ? sort.order : undefined} />
                        </span>
                      ) : (
                        column.label
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            </TableHeader>
          )}

          {reorder ? (
            <ReorderableBody
              groups={displayGroups}
              columns={visibleColumns}
              similarWarnings={similarWarnings}
              similarityGroups={similarityGroups}
              expanded={expanded}
              setExpanded={setExpanded}
              renderActions={renderActions}
              onRename={onRename}
              onLinkClick={onLinkClick}
              getCheckbox={selection ? (group, line) => getRowCheckbox(group, line, selection) : undefined}
              onSelectToggle={selection ? (line, next) => selection.onToggle(line, next) : undefined}
              onReorder={handleReorder}
              onOverviewsLoaded={handleOverviewsLoaded}
            />
          ) : (
            <TableBody>
              {lines.flatMap(({ line, groupKey, group }, index) => {
                const clusterId = similarityGroups?.get(line.address.toLowerCase())
                const bandHeader = bandHeaderAt(
                  index,
                  (i) => similarityGroups?.get(lines[i].line.address.toLowerCase()),
                  visibleColumns.length,
                )

                const row = (
                  <SafeAccountTableRow
                    key={line.key}
                    line={line}
                    columns={visibleColumns}
                    expanded={line.expandable ? expanded.has(groupKey) : undefined}
                    warning={similarWarnings?.get(line.address.toLowerCase())}
                    highlighted={Boolean(clusterId)}
                    renderActions={renderActions}
                    renderName={renderName}
                    onRename={onRename}
                    checkbox={selection ? getRowCheckbox(group, line, selection) : undefined}
                    onSelectToggle={selection ? (next) => selection.onToggle(line, next) : undefined}
                    onToggle={line.expandable ? () => toggle(groupKey) : undefined}
                    onLinkClick={onLinkClick}
                    showDivider={!embedded && index < lines.length - 1 && lines[index + 1].groupKey !== groupKey}
                    onOverviewsLoaded={handleOverviewsLoaded}
                  />
                )

                return bandHeader ? [bandHeader, row] : [row]
              })}
            </TableBody>
          )}
        </table>
      </div>

      {renameTarget && (
        <EntryDialog
          handleClose={() => setRenameTarget(null)}
          defaultValues={{ name: renameTarget.name, address: renameTarget.address }}
          chainIds={renameTarget.chainIds}
          scope={renameScope}
          disableAddressInput
          // In a modal surface, sit above the shadcn Dialog (--z-overlay) instead of behind it.
          className={allowRenameInDialog ? 'z-[var(--z-nested-overlay)]' : undefined}
          overlayClassName={allowRenameInDialog ? 'z-[var(--z-nested-overlay)]' : undefined}
        />
      )}
    </div>
  )
}
